import { useState, useEffect, KeyboardEvent, useMemo } from 'react'
import { IonCard, IonInput, IonText, IonSpinner } from '@ionic/react'
import { OrderItem, RedeemCode } from 'mytours-read-api-typescript'
import { LockAppContainer as Container, getDirectApi } from 'mytours-read-common'
import { ionHistory } from 'mobile_web/modules/navigation'
import PayModal from './PayModal'
import SuccessModal from './SuccessModal'
import { formatCurrency } from '../functions'
import Page from '../../common/components/Page'
import Button from '../../common/components/Button'
import { ImageSizePreset } from '../../../common_lib/types'
import { generateImageUrl } from '../../../common_lib/utils'
import strings from '../../../strings'

import classnames from 'classnames'
import '../styles.less'

interface ImageDimension {
	width: number
	height: number
}

const LockApp = ({
	title,
	description,
	backgroundImage,
	redeemCode,
	lockApp,
	linkedId,
	linkedType,
	webPrice,
	openPayModalImmediately,
	appMetaData,
}: Container.Props & Container.Actions) => {
	const [code, setCode] = useState<string>('')
	const [showEmailField, setShowEmailField] = useState<boolean>(false)
	const [email, setEmail] = useState<string>('')
	const [error, setError] = useState<string>('')
	const [message, setMessage] = useState<string>('')
	const [payModalOpen, setPayModalOpen] = useState<boolean>(false)
	const [successModalOpen, setSuccessModalOpen] = useState<boolean>(false)
	const [imageDimension, setImageDimension] = useState<ImageDimension>({
		width: null,
		height: null,
	})

	const handleChange =
		(prevValue: string, handler: (value: string) => void) =>
		(e: CustomEvent<any>): void => {
			const newValue = (e.target as HTMLInputElement).value
			// Check if the value has changed so that we don't clear out requires_email error without showing it to the user
			if (newValue !== prevValue) {
				setError('')
				return handler(newValue)
			}
		}

	const handleKeyUp = (e: KeyboardEvent<HTMLIonInputElement>): void => {
		if (e?.keyCode === 13) {
			onSent()
		}
	}

	useEffect(() => {
		const url = new URL(window.location.href)
		const searchParams = url.searchParams
		const orderId = Number(searchParams.get('order-id'))
		// Don't open PayModal if we are in the process of validating payment
		if (orderId) {
			return
		}

		if (webPrice && openPayModalImmediately) {
			setPayModalOpen(true)
		}
	}, [])

	useEffect(() => {
		if  (linkedType === RedeemCode.LinkedTypeEnum.App) {
			if (lockApp.app.loaded && lockApp.app.status === 'valid') {
				return
			}
		} else if (linkedType === RedeemCode.LinkedTypeEnum.Collection) {
			if (lockApp.collection.loaded && lockApp.collection.status === 'valid') {
				return
			}
		}

		const url = new URL(window.location.href)
		const searchParams = url.searchParams
		const orderId = Number(searchParams.get('order-id'))
		const paymentIntent = searchParams.get('payment_intent')
		if (!orderId) {
			return
		}

		setMessage(strings.validating_payment)

		url.searchParams.delete('order-id')
		url.searchParams.delete('payment_intent')
		url.searchParams.delete('payment_intent_client_secret')
		url.searchParams.delete('redirect_status')
		ionHistory.replace(url)

		const fetchCodeAndEmail = async () => {
			const order = await getDirectApi().orderShow(orderId, paymentIntent)
			// Order only has one item at the moment
			return {
				code: order.items?.[0].code,
				email: order.email
			}
		}

		const fetchAndRedeem = async () => {
			let retry = 0
			do {
				const { code, email } = await fetchCodeAndEmail()
				if (code) {
					setCode(code)
					setEmail(email)
					setSuccessModalOpen(true)
					return
				}
				retry++
				await new Promise(resolve => setTimeout(resolve, 2000 * retry))
			} while (retry < 3)
			
			setMessage(strings.validating_payment_failed)
		}

		fetchAndRedeem()
	}, [])

	const handleCloseSuccessModal = () => {
		redeemCode(code, linkedId, linkedType, email)
	}

	const { file } = backgroundImage || {}

	useEffect(() => {

		if (linkedType === RedeemCode.LinkedTypeEnum.Collection && linkedId !== lockApp.linkedId) {
			return
		}

		if (linkedType === RedeemCode.LinkedTypeEnum.App) {
			if (!lockApp.app.loading && lockApp.app.loaded && lockApp.app.status !== 'valid') {
				if (lockApp.app.status === 'expired') {
					setError(strings.code_expired)
				} else if (lockApp.app.status === 'max_redemptions') {
					setError(strings.code_max_redemptions)
				} else if (lockApp.app.status === 'requires_email') {
					setError(strings.code_requires_email)
					setCode(lockApp.couponCode)
					setShowEmailField(true)
				} else {
					setError(strings.code_invalid)
				}
			}
		} else if (linkedType === RedeemCode.LinkedTypeEnum.Collection) {
			if (!lockApp.collection.loading && lockApp.collection.loaded && lockApp.collection.status !== 'valid') {
				if (lockApp.collection.status === 'expired') {
					setError(strings.code_expired)
				} else if (lockApp.collection.status === 'max_redemptions') {
					setError(strings.code_max_redemptions)
				} else if (lockApp.collection.status === 'requires_email') {
					setError(strings.code_requires_email)
					setCode(lockApp.couponCode)
					setShowEmailField(true)
				} else {
					setError(strings.code_invalid)
				}
			}
		}
	}, [lockApp])

	useEffect(() => {
		setupImageDimension()
		addEventListener('resize', setupImageDimension)

		return () => {
			removeEventListener('resize', setupImageDimension)
		}
	}, [imageDimension])

	const setupImageDimension = (): void => {
		const width: number = window.innerWidth
		const height: number = window.innerHeight

		if (imageDimension.width !== width || imageDimension.height !== height) {
			setImageDimension({ width, height })
		}
	}

	const loading =
		(linkedType === RedeemCode.LinkedTypeEnum.Collection && lockApp.collection.loading) ||
		(linkedType === RedeemCode.LinkedTypeEnum.App && lockApp.app.loading)

	const onSent = (): void => {
		if (loading) {
			return
		}

		redeemCode(code, linkedId, linkedType, email)
	}

	const titleNode = useMemo(() => {
		const defaultTitle =
			(linkedType === RedeemCode.LinkedTypeEnum.Collection && strings.unlock_the_collection) ||
			(linkedType === RedeemCode.LinkedTypeEnum.App && strings.unlock_the_app)
		return (title || defaultTitle) && <h1>{title || defaultTitle}</h1>
	}, [title])

	const bgImagePath =
		file?.original_url &&
		`${generateImageUrl(file, {
			imageSizePreset: ImageSizePreset.Fullscreen,
			blur: 10,
		})}`

	const price = webPrice ? formatCurrency(webPrice.currency, webPrice.amount) : ''

	const togglePayModal = (): void => {
		setPayModalOpen(!payModalOpen)
	}

	const payModalLinkedType = linkedType === RedeemCode.LinkedTypeEnum.App ? OrderItem.ItemTypeEnum.Project : linkedType as unknown as OrderItem.ItemTypeEnum
	const payModalLinkedId = (linkedType === RedeemCode.LinkedTypeEnum.App && appMetaData) ? appMetaData.project_id : linkedId

	return (
		<Page
			contentProps={{
				className: classnames('lock-app'),
				style: {
					background: `url(${bgImagePath}) no-repeat center center / cover`,
					'--content-background-image': 'url()'
				},
			}}
			dataCy="page-lock-app"
		>
			<IonCard className="center-block">
				<i className="lock-icon fal lock-alt" />
				{titleNode}
				<p>{description || strings.enter_your_access_code_to_continue}</p>
				<IonInput value={code} onIonChange={handleChange(code, setCode)} onKeyUp={handleKeyUp}></IonInput>
				{showEmailField && <IonInput value={email} onIonChange={handleChange(email, setEmail)} onKeyUp={handleKeyUp} placeholder={`${strings.email}:`} />}
				{error && <div className="error">{error}</div>}
				{message && <div>{message}</div>}

				<Button className="lock-button" ariaLabel={strings.unlock} onClick={onSent}>
					<IonText>{strings.unlock}</IonText>
					{loading && <IonSpinner name="crescent" />}
				</Button>

				{webPrice ? (
					<>
						<br />
						<Button className="buy-button" ariaLabel={strings.formatString(strings.buy_for, { price }) as string} onClick={togglePayModal}>
							<IonText>{strings.formatString(strings.buy_for, { price })}</IonText>
							{loading && <IonSpinner name="crescent" />}
						</Button>
					</>
				) : null}
				{payModalOpen && webPrice && (
					<PayModal
						titleNode={titleNode}
						webPrice={webPrice}
						itemId={payModalLinkedId}
						itemType={payModalLinkedType}					
						onClose={togglePayModal}
					/>
				)}
				{successModalOpen && <SuccessModal onCloseSuccessModal={handleCloseSuccessModal} />}
			</IonCard>
		</Page>
	)
}

const LockAppComponent = Container.default(LockApp)
export default LockAppComponent
